import { useAssertedParams } from '@folio/lib';
import { useQuery } from '@tanstack/react-query';
import {
  policyControllerGetPolicyQueryOptions,
  BrokerageFirmDtoFirmTypeEnumKey,
  BranchDtoBranchStatusEnumKey,
  BranchDtoBranchTypeEnumKey,
  PolicyDtoStatusEnumKey,
  DerogationStatusEnumKey,
  DerogationActionEnumKey,
  derogationControllerCheckDerogationRequirementQueryOptions,
  brokerageFirmControllerGetBrokerageFirmQueryOptions,
  legacyPermissionsControllerAtMeQueryOptions,
  policyControllerListBranchesQueryOptions,
  derogationStatusEnum,
  derogationActionEnum,
} from 'korint-sdk';
import { isWaitingForQuoteDerogation } from './isWaitingForQuoteDerogation';

export function useVisibilityConditionsProps({
  action,
  overridenBranchId,
}: {
  action: DerogationActionEnumKey;
  overridenBranchId?: string;
}): {
  firmType: BrokerageFirmDtoFirmTypeEnumKey;
  policyStatus: PolicyDtoStatusEnumKey;
  branchStatus: BranchDtoBranchStatusEnumKey;
  branchType: BranchDtoBranchTypeEnumKey | undefined;
  isDerogationSatisfied: boolean;
  derogationStatus: DerogationStatusEnumKey | undefined;
  shouldCreateDerogation: boolean;
} | null {
  const { policyId, branchId: assertedBranchId } = useAssertedParams();
  const branchId = overridenBranchId ?? assertedBranchId;

  const { data: policyStatus } = useQuery({
    ...policyControllerGetPolicyQueryOptions(policyId, { branchId }),
    select: data => data.status,
    enabled: Boolean(branchId),
  });

  const { data: needsDerogation } = useQuery({
    ...derogationControllerCheckDerogationRequirementQueryOptions(
      branchId,
      {},
      {
        action,
      },
    ),
    select: data => data.needsDerogation,
    enabled: Boolean(branchId),
  });

  const { data: needsQuoteDerogation } = useQuery({
    ...derogationControllerCheckDerogationRequirementQueryOptions(
      branchId,
      {},
      { action: derogationActionEnum.QUOTE },
    ),
    select: data => data.needsDerogation,
    enabled: Boolean(branchId) && action === derogationActionEnum.CONFIRM,
  });

  const { data: { brokerageFirmId } = {} } = useQuery({
    ...legacyPermissionsControllerAtMeQueryOptions(),
  });

  const { data: firmType } = useQuery({
    ...brokerageFirmControllerGetBrokerageFirmQueryOptions(brokerageFirmId!),
    enabled: !!brokerageFirmId,
    select: data => data.firmType,
  });

  const { data: branches } = useQuery({
    ...policyControllerListBranchesQueryOptions(policyId),
  });

  const workingBranch = branches?.data.find(branch => branch.id === branchId);

  const derogations =
    workingBranch?.derogations?.filter(derogation => derogation.action === action) ?? [];

  const hasDerogationOnBranch = (workingBranch?.derogations?.length ?? 0) > 0;

  const hasApprovedDerogationOnBranch =
    workingBranch?.derogations?.some(({ status }) => status === derogationStatusEnum.APPROVED) ??
    false;

  if (!firmType || !policyStatus || needsDerogation === undefined || !workingBranch?.branchStatus) {
    return null;
  }

  const isActionDerogationApproved = derogations.some(
    ({ status }) => status === derogationStatusEnum.APPROVED,
  );

  const isDerogationSatisfied =
    (hasApprovedDerogationOnBranch || (!needsDerogation && !hasDerogationOnBranch)) &&
    (!needsDerogation || isActionDerogationApproved);

  return {
    firmType,
    isDerogationSatisfied,
    policyStatus,
    branchStatus: workingBranch.branchStatus,
    branchType: workingBranch.branchType,
    derogationStatus: derogations[0]?.status,
    shouldCreateDerogation:
      needsDerogation &&
      derogations.length === 0 &&
      !isWaitingForQuoteDerogation({
        action,
        needsQuoteDerogation,
        derogations: workingBranch.derogations ?? [],
      }),
  };
}
